import type {
    CacOrder,
    ClickAndCollectPromotion,
    CreditCardInfo,
    DeliveryFees,
    DlvOrder,
    OrderJobDriverResponse,
    OrderRestaurantShortDTO,
    ProfileUserForm,
    StoredPaymentMethod
} from 'shared/types'
import { orderPickUpType } from 'shared/utils'
import type { Action } from './actions'
import type {
    EcommOrder,
    ItemInfos,
    MissingIds,
    PurchasableItem
} from './types'

const { DELIVERY_IN_APP } = orderPickUpType

export type State = {
    error?: {
        message?: string,
        reason?: string
    },
    loader: {
        isLoading: boolean,
        intlKeyMessage?: string
    },
    restaurant?: OrderRestaurantShortDTO,
    ecommerceMode: string,
    collectMode?: string,
    pickUpInfo?: string,
    ordersJobs?: { [string]: Array<OrderJobDriverResponse> },
    lastTimeCartModified?: number,
    products: Array<PurchasableItem>,
    userInfos?: ProfileUserForm,
    currentItemInfos?: ItemInfos,
    currentItem?: PurchasableItem,
    currentSuggestions: Array<PurchasableItem>,
    cacOrders: { [string]: CacOrder },
    deliveryOrders: { [string]: DlvOrder },
    cards: Array<CreditCardInfo>,
    isCartToastVisible?: boolean,
    randomCoupon: {
        showModal: boolean,
        isWinner: boolean,
        imageUrl?: string,
        imageLabel?: string,
        operationRepeatAttributionType?: string
    },
    currentToken?: string,
    idsMissingProducts?: MissingIds,
    promotions: Array<ClickAndCollectPromotion>,
    idsCouponsTemporaryBurned: Array<string>,
    parkingOpen: boolean,
    hasLastOrder: boolean,
    minimumPrice?: number,
    maximumPrice?: number,
    deliveryAddress?: string,
    deliveryInfos?: {
        infos?: string,
        dropOffType: string
    },
    deliveryFees: { fees?: DeliveryFees, feesPurchasable?: PurchasableItem },
    hideNeptingCloseButton?: boolean,
    adyen: {
        redirection?: {
            orderId?: string,
            orderToken?: string
        },
        paymentResponse?: any,
        paymentMethods?: any,
        storedPaymentMethods?: StoredPaymentMethod[]
    }
}

const initialState: State = {
    loader: {
        isLoading: false
    },
    error: undefined,
    restaurant: undefined,
    ecommerceMode: '',
    collectMode: undefined,
    pickUpInfo: undefined,
    ordersJobs: {},
    products: [],
    cacOrders: {},
    deliveryOrders: {},
    userInfos: undefined,
    currentItemInfos: undefined,
    currentSuggestions: [],
    cards: [],
    isCartToastVisible: false,
    randomCoupon: {
        showModal: false,
        isWinner: false,
        imageUrl: '',
        imageLabel: '',
        operationRepeatAttributionType: undefined
    },
    currentToken: undefined,
    lastTimeCartModified: undefined,
    idsMissingProducts: undefined,
    promotions: [],
    idsCouponsTemporaryBurned: [],
    parkingOpen: true,
    hasLastOrder: false,
    deliveryAddress: undefined,
    deliveryInfos: undefined,
    deliveryFees: {},
    adyen: {},
    hideNeptingCloseButton: false
}

export const reducer = (state: State = initialState, action: Action): State => {
    switch (action.type) {
        case 'ECOMM_SET_ERROR':
            return {
                ...state,
                error: action.error
            }
        case 'ECOMM_SET_LOADER':
            return {
                ...state,
                loader: action.loader
            }
        case 'ECOMM_SET_ECOMMERCE_MODE':
            return {
                ...state,
                ecommerceMode: action.ecommerceMode
            }
        case 'ECOMM_SET_DELIVERY_ADDRESS':
            return {
                ...state,
                deliveryAddress: action.deliveryAddress
            }
        case 'ECOMM_SET_DELIVERY_INFOS':
            return {
                ...state,
                deliveryInfos: action.deliveryInfos
            }
        case 'ECOMM_SET_DELIVERY_FEES':
            return {
                ...state,
                deliveryFees: {
                    fees: action.deliveryFees,
                    feesPurchasable: action.deliveryFeesPurchasable
                }
            }
        case 'ECOMM_SET_RESTAURANT':
            return {
                ...state,
                restaurant: action.restaurant
            }
        case 'ECOMM_SET_COLLECT_MODE':
            return {
                ...state,
                collectMode: action.collectMode,
                pickUpInfo: action.pickUpInfo ?? undefined
            }
        case 'ECOMM_SET_USER_INFOS':
            return {
                ...state,
                userInfos: action.userInfos
            }
        case 'ECOMM_ADD_PRODUCT_TO_CART':
        case 'ECOMM_REMOVE_PRODUCT_FROM_CART':
        case 'ECOMM_UPDATE_CART_CONTENT':
            return {
                ...state,
                products: action.products,
                lastTimeCartModified: new Date().getTime()
            }
        case 'ECOMM_SET_CURRENT_ITEM_INFOS':
            return { ...state, currentItemInfos: action.itemInfos }
        case 'ECOMM_SET_CURRENT_PRODUCT':
            return { ...state, currentItem: action.product }
        case 'ECOMM_RESET_CURRENT_PRODUCT':
            return {
                ...state,
                currentItem: undefined,
                currentItemInfos: undefined,
                currentSuggestions: []
            }
        case 'ECOMM_SET_CURRENT_SUGGESTIONS':
            return { ...state, currentSuggestions: action.suggestions }
        case 'ECOMM_SET_RANDOM_COUPON':
            return {
                ...state,
                randomCoupon: {
                    ...state.randomCoupon,
                    ...action.randomCoupon
                }
            }
        case 'ECOMM_SET_CART_TOAST':
            return {
                ...state,
                isCartToastVisible: action.isCartToastVisible
            }
        case 'ECOMM_UPDATE_CURRENT_ORDER': {
            const { currentToken } = state
            const { order } = action
            const { cards, pickUpType } = order
            const token = order.token || currentToken
            const isDelivery = pickUpType === DELIVERY_IN_APP

            const orderToUpdate = {}
            if (token) {
                orderToUpdate[token] = order
            }
            return {
                ...state,
                couponsTemporaryBurn: undefined,
                currentToken: token,
                cards: cards || state.cards,
                ...(isDelivery
                    ? {
                          deliveryOrders: {
                              ...state.deliveryOrders,
                              ...orderToUpdate
                          }
                      }
                    : {
                          cacOrders: { ...state.cacOrders, ...orderToUpdate }
                      })
            }
        }
        case 'ECOMM_REMOVE_CURRENT_ORDER_TOKEN':
            return {
                ...state,
                currentToken: undefined
            }
        case 'ECOMM_UPDATE_ORDER':
            const { order } = action
            const { pickUpType } = order
            const isDelivery = pickUpType === DELIVERY_IN_APP
            return {
                ...state,
                ...(isDelivery
                    ? {
                          deliveryOrders: {
                              ...state.deliveryOrders,
                              [order.token]: order
                          }
                      }
                    : {
                          cacOrders: {
                              ...state.cacOrders,
                              [order.token]: order
                          }
                      })
            }
        case 'ECOMM_UPDATE_ORDERS':
            const { orders } = action
            return {
                ...state,
                ...(action.isDelivery
                    ? {
                          deliveryOrders: {
                              ...orders
                          }
                      }
                    : {
                          cacOrders: {
                              ...orders
                          }
                      })
            }
        case 'ECOMM_HAS_LAST_ORDER':
            return {
                ...state,
                hasLastOrder: action.hasLastOrder
            }
        case 'ECOMM_HANDLE_MISSING_PRODUCTS':
            return {
                ...state,
                idsMissingProducts: action.idsMissingProducts,
                products: action.products
            }
        case 'ECOMM_RESET_CART': {
            return {
                ...initialState,
                loader: state.loader,
                userInfos: state.userInfos,
                cacOrders: state.cacOrders,
                currentToken: undefined,
                deliveryOrders: state.deliveryOrders,
                ecommerceMode: state.ecommerceMode
            }
        }
        case 'ECOMM_RESET_ORDER': {
            return {
                ...state,
                currentToken: undefined
            }
        }
        case 'ECOMM_ORDER_REJECTED': {
            return {
                ...state,
                currentToken: undefined
            }
        }
        case 'ECOMM_SET_PROMOTIONS':
            return {
                ...state,
                promotions: action.promotions
            }
        case 'ECOMM_HANDLE_COUPON':
            return {
                ...state,
                idsCouponsTemporaryBurned: action.coupons
            }
        case 'ECOMM_DISCARD_ORDER': {
            const { id, pickUpType } = action
            const isDelivery = pickUpType === DELIVERY_IN_APP
            let found
            if (isDelivery) {
                found =
                    state.deliveryOrders &&
                    Object.entries(state.deliveryOrders).find(
                        ([token: string, order: DlvOrder]) => {
                            //$FlowFixMe
                            return order.id === id
                        }
                    )
            } else {
                found =
                    state.cacOrders &&
                    Object.entries(state.cacOrders).find(
                        ([token: string, order: CacOrder]) => {
                            //$FlowFixMe
                            return order.id === id
                        }
                    )
            }
            if (!found) {
                return state
            }
            const token = found[0]
            return {
                ...state,
                ...(isDelivery
                    ? {
                          deliveryOrders: {
                              ...state.deliveryOrders,
                              [token]: {
                                  ...state.deliveryOrders[token],
                                  discarded: true
                              }
                          }
                      }
                    : {
                          cacOrders: {
                              ...state.cacOrders,
                              [token]: {
                                  ...state.cacOrders[token],
                                  discarded: true
                              }
                          }
                      })
            }
        }
        case 'ECOMM_UPDATE_ORDER_ORB_STATUS': {
            const { token, status, pickUpType } = action
            const isDelivery = pickUpType && pickUpType === DELIVERY_IN_APP
            return {
                ...state,
                ...(isDelivery
                    ? {
                          deliveryOrders: {
                              ...state.deliveryOrders,
                              [token]: {
                                  ...state.deliveryOrders[token],
                                  orbStatus: status
                              }
                          }
                      }
                    : {
                          cacOrders: {
                              ...state.cacOrders,
                              [token]: {
                                  ...state.cacOrders[token],
                                  orbStatus: status
                              }
                          }
                      })
            }
        }
        //Update ORB Status
        case 'ORB_EVENT_UPDATE_ORDER':
            const { orbNb, orderId, status } = action.data.order
            const cacOrderFound =
                state.cacOrders &&
                Object.entries(state.cacOrders).find(
                    ([token: string, order: CacOrder]) => {
                        //$FlowFixMe
                        return order.id === orderId
                    }
                )
            const deliveryOrderFound =
                state.deliveryOrders &&
                Object.entries(state.deliveryOrders).find(
                    ([token: string, order: DlvOrder]) => {
                        //$FlowFixMe
                        return order.id === orderId
                    }
                )
            const found = cacOrderFound || deliveryOrderFound
            if (!found) {
                return state
            }
            const token = found[0]
            return {
                ...state,
                ...(deliveryOrderFound
                    ? {
                          deliveryOrders: {
                              ...state.deliveryOrders,
                              [token]: {
                                  ...state.deliveryOrders[token],
                                  orbStatus: status,
                                  orderNumber: orbNb
                              }
                          }
                      }
                    : {
                          cacOrders: {
                              ...state.cacOrders,
                              [token]: {
                                  ...state.cacOrders[token],
                                  orbStatus: status,
                                  orderNumber: orbNb
                              }
                          }
                      })
            }
        case 'ECOMM_PARKING_OPEN':
            return {
                ...state,
                parkingOpen: action.parkingOpen
            }
        case 'ECOMM_FETCH_PRICE_EDGES':
            return {
                ...state,
                ...action.priceEdges
            }
        case 'ECOMM_FETCH_DRIVERS':
            const { orderJobs } = action
            return {
                ...state,
                ordersJobs: {
                    ...state.ordersJobs,
                    [orderJobs[0].orderId]: orderJobs
                }
            }
        case 'ECOMM_SET_RECEIPT_URL': {
            const { orderId, receiptUrl, target } = action
            //$FlowFixMe
            const { token } = Object.values(state[`${target}`]).find(
                //$FlowFixMe
                (item: EcommOrder) => item?.id === orderId
            )

            return {
                ...state,
                [target]: {
                    ...state[target],
                    [token]: {
                        ...state[target][token],
                        receiptUrl
                    }
                }
            }
        }
        case 'ECOMM_SET_HIDE_NEPTING_CLOSE_BUTTON':
            return {
                ...state,
                hideNeptingCloseButton: action.hideNeptingCloseButton
            }
        case 'ECOMM_ADYEN_SET_REDIRECTION':
            return {
                ...state,
                adyen: {
                    ...state.adyen,
                    redirection: action.redirection
                }
            }
        case 'ECOMM_ADYEN_PAYMENT_METHODS':
            return {
                ...state,
                adyen: {
                    ...state.adyen,
                    paymentMethods: action.paymentMethods
                }
            }
        case 'ECOMM_ADYEN_PAYMENT_RESPONSE':
            return {
                ...state,
                adyen: {
                    ...state.adyen,
                    paymentResponse: action.paymentResponse
                }
            }
        case 'LOGOUT':
            let ordersNotConnected = {
                ...state.cacOrders,
                ...state.deliveryOrders
            }
            Object.values(state.cacOrders).forEach(order => {
                //$FlowFixMe
                if (order && order.clientId && order.token) {
                    delete ordersNotConnected[order.token]
                }
            })
            return {
                ...initialState,
                cacOrders: ordersNotConnected
            }
        default:
            return state
    }
}
